import React, { useRef, useState } from 'react';
import { US_STATES } from '../../lib/usStates';
import { FileText, Upload, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api';

export interface TransferredRx {
  source: 'pdf' | 'manual';
  prescriber: {
    firstName: string;
    lastName: string;
    npiNumber: string;
    licenseState?: string;
    phone?: string;
  };
  documentFileName?: string;
  documentS3Key?: string;
  documentUrl?: string;
  notes?: string;
}

export const emptyTransferredRx = (source: 'pdf' | 'manual'): TransferredRx => ({
  source,
  prescriber: { firstName: '', lastName: '', npiNumber: '', licenseState: '' }
});

// Client-side NPI check (10 digits + CMS Luhn), mirroring the backend so a
// mistyped prescriber NPI is caught before the order is submitted.
export function isValidNpi(npi: string): boolean {
  if (!/^\d{10}$/.test(npi)) return false;
  if (!['1', '2'].includes(npi[0])) return false;
  const base = '80840' + npi.slice(0, 9);
  const digits = base.split('').map(Number);
  let sum = 0;
  for (let i = digits.length - 1, dbl = true; i >= 0; i--, dbl = !dbl) {
    let d = digits[i];
    if (dbl) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return (sum + Number(npi[9])) % 10 === 0;
}

// Returns a physician-facing error, or null when the transfer is complete.
// Mirrors normalizePrescriptionTransfer on the backend.
export function validateTransferredRx(t: TransferredRx): string | null {
  if (!t.prescriber.firstName.trim() || !t.prescriber.lastName.trim()) {
    return "Enter the prescribing doctor's first and last name.";
  }
  if (!isValidNpi(t.prescriber.npiNumber.trim())) {
    return "Enter a valid 10-digit NPI for the prescribing doctor.";
  }
  if (t.source === 'pdf' && !t.documentS3Key) {
    return 'Upload the prescription document, or switch to entering it manually.';
  }
  return null;
}

interface TransferredRxPanelProps {
  value: TransferredRx | null;
  onChange: (value: TransferredRx | null) => void;
}

const fieldCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

/**
 * Pharmacy accounts only: attach the outside doctor's prescription this order
 * fills. The doctor is not a Bloom user and is not id-proofed here — the
 * transferring pharmacy verified them in its own software — so their NPI is the
 * identity that goes to the fulfilling pharmacy in place of the account's own.
 *
 * Optional: a pharmacy ordering under its own NPI simply leaves this off.
 */
export const TransferredRxPanel: React.FC<TransferredRxPanelProps> = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const enabled = value !== null;

  const setPrescriber = (patch: Partial<TransferredRx['prescriber']>) => {
    if (!value) return;
    onChange({ ...value, prescriber: { ...value.prescriber, ...patch } });
  };

  const handleFile = async (file: File) => {
    if (!value) return;
    setUploadError('');
    setUploading(true);
    try {
      const res = await apiClient.uploadRxTransferDocument(file);
      if (!res.success || !res.data) {
        setUploadError(res.error || 'Upload failed.');
        return;
      }
      onChange({
        ...value,
        documentFileName: res.data.fileName,
        documentS3Key: res.data.fileKey,
        documentUrl: res.data.fileUrl
      });
    } catch (e: any) {
      setUploadError(e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const clearDocument = () => {
    if (!value) return;
    onChange({ ...value, documentFileName: undefined, documentS3Key: undefined, documentUrl: undefined });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? emptyTransferredRx('pdf') : null)}
        />
        <span>
          <span className="font-medium text-gray-900">This order fills a transferred prescription</span>
          <span className="block text-sm text-gray-500">
            Attach the script one of your doctors wrote. Their NPI goes to the fulfilling pharmacy as the
            prescriber; without it the order is written under your pharmacy's own NPI.
          </span>
        </span>
      </label>

      {enabled && value && (
        <div className="mt-4 space-y-4">
          {/* How the script reaches us */}
          <div className="flex gap-2">
            {(['pdf', 'manual'] as const).map((src) => (
              <button
                key={src}
                type="button"
                onClick={() => onChange({ ...value, source: src })}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                  value.source === src
                    ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                }`}
              >
                {src === 'pdf' ? <Upload className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                {src === 'pdf' ? 'Upload the script' : 'Enter it manually'}
              </button>
            ))}
          </div>

          {value.source === 'pdf' && (
            <div>
              {value.documentS3Key ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  <span className="flex-1 truncate text-gray-800">{value.documentFileName}</span>
                  <button type="button" onClick={clearDocument} className="text-gray-400 hover:text-red-500" aria-label="Remove document">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFile(file);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG — up to 10MB.</p>
                </div>
              )}
              {uploading && (
                <p className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                </p>
              )}
              {uploadError && (
                <p className="flex items-center gap-2 text-sm text-red-600 mt-2">
                  <AlertCircle className="w-4 h-4" /> {uploadError}
                </p>
              )}
            </div>
          )}

          {/* Prescriber identity — required either way. On the PDF path this is
              keyed from the uploaded script; we don't parse the document. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prescriber first name *</label>
              <input className={fieldCls} value={value.prescriber.firstName}
                onChange={(e) => setPrescriber({ firstName: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prescriber last name *</label>
              <input className={fieldCls} value={value.prescriber.lastName}
                onChange={(e) => setPrescriber({ lastName: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prescriber NPI *</label>
              <input className={fieldCls} inputMode="numeric" maxLength={10} placeholder="10 digits"
                value={value.prescriber.npiNumber}
                onChange={(e) => setPrescriber({ npiNumber: e.target.value.replace(/\D/g, '') })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">License state</label>
              <select className={fieldCls} value={value.prescriber.licenseState || ''}
                onChange={(e) => setPrescriber({ licenseState: e.target.value })}>
                <option value="">Select…</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes for the pharmacy</label>
            <textarea className={fieldCls} rows={2} maxLength={1000}
              placeholder="Original fill date, refills remaining on the paper script, etc."
              value={value.notes || ''}
              onChange={(e) => onChange({ ...value, notes: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
};
